import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  AutomationActionDenormalized,
  AutomationActionStatus,
  AutomationActionTemplate,
  AutomationStatus,
  ExperienceAnchorType,
  IAutomation,
  IAutomationTemplate,
  IExperience,
  IExperienceTemplate,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import {
  PAEventType,
  ProductAnalyticsEvent
} from '@dadohr/shared/domainTypes/productAnalytics';
import { IUser } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import { compact, flatten, groupBy, orderBy, uniq } from 'lodash';
import { AlertCircle } from 'lucide-react';
import moment from 'moment-timezone';
import { useCallback, useMemo } from 'react';
import { ColoredChipLight } from '../../../../components/ColoredChip';
import {
  ItemSorter,
  RowsRenderer,
  ROW_HEIGHTS,
  Sorters
} from '../../../../components/GroupableList';
import { IColumn } from '../../../../components/Table/VirtualizedSortable';
import { SortDirection, useSort } from '../../../../hooks/useSort';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { useCurrentUser } from '../../../../services/currentUser';
import {
  collectReplacements,
  getAllActorUserIds,
  isExperienceUserActor,
  toDenormalizedAutomationActionWithInterpolations,
  useExperienceInstanceById
} from '../../../../services/experiences/instance';
import { usePeopleTabV2Enabled } from '../../../../services/experiences/timelineItemStatus';
import { useMappedLoadingValue } from '../../../../services/firebase';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import {
  ExperienceReplacements,
  InterpolationDefaultOptions
} from '../../../../services/set/interpolation';
import { toMoment } from '../../../../services/time';
import { toFullName, useUserMapById } from '../../../../services/user';
import {
  ActionErrorDetails,
  ActionWithIcon
} from '../../pages/Details/ParticipantDialog';
import {
  ErrorText,
  InnerItemDetail,
  InnerItemList,
  InnerItemRow
} from '../../pages/Details/ParticipantDialogComponents/CommonStyledComponents';
import { AutomationStatusChip } from '../AutomationStatusChip';
import { AnchorDateDiffWarning } from '../ParticipantsTable';
import { GroupDivider } from './components/GroupDivider';
import { TimeZone } from './components/Timezone';
import { Participant, SubItemContainer } from './Tasks';

const InnerItemGroup = styled('div')((p) => ({
  paddingLeft: p.theme.spacing(1),
  paddingTop: p.theme.spacing(1)
}));

export const ErrorAlert = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2px;
}
`;

type ColumnName =
  | 'participant'
  | 'firstDayAtWork'
  | 'runDate'
  // | 'department' Commenting out for now since not part of ticket scope, but might be used in a later point in time
  | 'supportingActorName';

type D = {
  exp: Doc<IExperience>;
  actors: { [actorKey: string]: Doc<IUser> | null };
};

type Group = {
  key: string;
  rows: D[];
};

type OtherProps = {
  tpl: Doc<IExperienceTemplate>;
  autoTplId: string;
  actor: { key: string; label: string };
  onShowInstanceDetails: (d: Doc<IExperience>) => void;
  trackEvent: (event: ProductAnalyticsEvent) => Promise<void>;
};

const automationActionWithQueryField = (
  automation: IAutomation | undefined,
  automationTpl: IAutomationTemplate | undefined,
  replacements: ExperienceReplacements,
  interpolationOptions: InterpolationDefaultOptions
): AutomationActionDenormalized[] => {
  const result = compact(
    Object.values(automation?.actions || {}).map((auto) => {
      const autoTpl = automationTpl?.actions.find(
        (action: AutomationActionTemplate) => action.id === auto.templateId
      );
      if (!autoTpl) {
        return;
      }

      const result = toDenormalizedAutomationActionWithInterpolations(
        autoTpl,
        auto,
        replacements,
        {},
        interpolationOptions
      );
      return result;
    })
  );
  return result;
};
export const formatAutomationToExpandableComponent = (
  a: D[],
  autoTplId: string,
  tpl: Doc<IExperienceTemplate>,
  org: IOrganization
) => {
  const automationTpl = tpl.data.automations.find((a) => a.id === autoTplId);

  return new Map(
    a.map((row) => {
      let automation = Object.values(row.exp.data.automations).find(
        (auto) => auto.templateId === autoTplId
      );
      const actors: { [key: string]: Doc<IUser> } = {};
      Object.entries(row.actors).forEach(([k, a]) => {
        if (a) {
          actors[k] = a;
        }
      });
      const replacements = collectReplacements(
        tpl,
        row.exp,
        org.name,
        actors,
        org.customUserFields,
        row.exp.data.anchorDate.at
      );

      const answers: string[] = [];
      row.exp.data.tasks.forEach((t) => {
        t.subTasks.forEach((s) => {
          if (s.type === 'QUESTION') {
            answers.push(s.id);
          }
        });
      });

      const options = {
        dateFormat: org.dateFormats.MMDDYYYY
      };
      const actions = automationActionWithQueryField(
        automation,
        automationTpl,
        replacements,
        options
      );
      return [
        row.exp.id,
        <ExtendableAutomationsActions
          actions={actions ? actions : []}
          supportingActors={tpl.data.supportingActors}
        />
      ];
    })
  );
};

// Lazy per-row expand for People Tab v2: rows come from compact projections
// (no action content / errorData / QUESTION answers), so when a row is expanded
// we load that ONE full instance and reuse the existing formatter on it.
const LazyAutomationRowExpand = ({
  row,
  autoTplId,
  tpl,
  org
}: {
  row: D;
  autoTplId: string;
  tpl: Doc<IExperienceTemplate>;
  org: IOrganization;
}) => {
  const [full] = useExperienceInstanceById(row.exp.id);
  if (!full) {
    return null;
  }
  const element = formatAutomationToExpandableComponent(
    [{ ...row, exp: full }],
    autoTplId,
    tpl,
    org
  ).get(full.id);
  return <>{element ?? null}</>;
};

const withActorOrNull = <T extends unknown>(
  d: D,
  actor: string,
  fn: (user: Doc<IUser>) => T
): T | null => {
  const a = d.actors[actor];
  return a ? fn(a) : null;
};

const withParticipantOrNull = <T extends unknown>(
  d: D,
  fn: (user: Doc<IUser>) => T
): T | null => withActorOrNull(d, PARTICIPANT_ACTOR_KEY, fn);

type Column = IColumn<D, ColumnName, OtherProps>;

const PARTICIPANT_COLUMNS: Column[] = [
  {
    key: 'participant',
    head: () => 'Participant',
    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        return (
          <FlexContainer>
            <>
              {d.exp.data.automations !== null && (
                <AutomationStatusChip
                  autoWidth={true}
                  status={
                    d.exp.data.automations[`${o.autoTplId}`].status ===
                    'UPCOMING'
                      ? AutomationStatus.UPCOMING
                      : d.exp.data.automations[`${o.autoTplId}`].status ===
                        'ERROR'
                      ? AutomationStatus.ERROR
                      : d.exp.data.automations[`${o.autoTplId}`].status ===
                        'DONE'
                      ? AutomationStatus.DONE
                      : // TRIGGERED is a pending trigger-chain automation, not a
                        // skip — it was falling through to the SKIPPED default and
                        // rendering the wrong "Skipped" pill.
                      d.exp.data.automations[`${o.autoTplId}`].status ===
                        'TRIGGERED'
                      ? AutomationStatus.TRIGGERED
                      : AutomationStatus.SKIPPED
                  }
                />
              )}
            </>
            <Participant
              participant={
                toFullName(u.data) || u.data.email || u.data.personalEmail
              }
              onClick={() => {
                o.onShowInstanceDetails(d.exp);
                o.trackEvent({
                  eventType: PAEventType.EXPERIENCE_INSTANCE,
                  name: 'Admin Instance View Loaded',
                  'Instance ID': d.exp.id,
                  Trigger: 'Timeline Item People Tab',
                  Explanation:
                    'Admin user views the details of a single instance'
                });
              }}
            />
          </FlexContainer>
        );
      }),
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  {
    key: 'firstDayAtWork',
    head: () => 'First day at work',
    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        const currentUser = useCurrentUser();
        const { org } = currentUser;
        if (
          o.tpl.data.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE &&
          o.tpl.data.anchor.field === 'hireDate'
        ) {
          const originalDate =
            u.data.hireDate && toMoment(u.data.hireDate).tz(o.tpl.data.tz);
          // This is a rare exception for broken instances which could NOT be migrated when this field was introduced
          if (!d.exp.data.anchorDate?.at) {
            return (
              <FlexContainer>
                <div>{originalDate?.format(org.dateFormats.MMDDYYYY)}</div>
              </FlexContainer>
            );
          }
          const currentDate = toMoment(d.exp.data.anchorDate.at).tz(
            o.tpl.data.tz
          );
          return (
            <FlexContainer>
              <div>{currentDate.format(org.dateFormats.MMDDYYYY)}</div>
              <AnchorDateDiffWarning
                original={originalDate}
                current={currentDate}
              />
            </FlexContainer>
          );
        }
        return (
          u.data.hireDate &&
          toMoment(u.data.hireDate).format(org.dateFormats.MMDDYYYY)
        );
      }),
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },

  {
    key: 'runDate',
    head: () => 'Run date',
    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        const currentUser = useCurrentUser();
        const { org } = currentUser;
        return (
          <FlexContainer>
            {toMoment(d.exp.data.automations[o.autoTplId].timing.at)
              .tz(o.tpl.data.tz)
              .format(`${org.dateFormats.MMDDYYYY} HH:mm`)}
            <TimeZone>
              {' '}
              {toMoment(d.exp.data.automations[o.autoTplId].timing.at)
                .tz(o.tpl.data.tz)
                .format('z')}
            </TimeZone>
          </FlexContainer>
        );
      }),
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  }

  //Commenting out for now since not part of ticket scope, but might be used in a later point in time

  // {
  //   key: 'department',
  //   head: () => 'Department',
  //   cell: (d) => withParticipantOrNull(d, (u) => u.data.department),
  //   align: 'left',
  //   sortable: true,
  //   defaultDirection: 'asc',
  //   width: 150,
  //   flexGrow: 1
  // }
];

const SUPPORTING_ACTOR_COLUMNS: Column[] = [
  {
    key: 'supportingActorName',
    head: (o) => o?.actor.label,
    cell: (d, o) => {
      return withActorOrNull(d, o.actor.key, (u) => toFullName(u.data));
    },
    align: 'left',
    sortable: true,
    width: 150,
    flexGrow: 1
  },
  ...PARTICIPANT_COLUMNS
];

type GroupSummary = {
  count: number;
};

const SORTERS: Sorters<GroupSummary, D, OtherProps> = {
  firstDayAtWork: {
    key: 'firstDayAtWork',
    label: 'First day at work',
    groups: { sort: (g) => g.key, dir: 'asc' },
    items: {
      sort: (d) =>
        withParticipantOrNull(d, (u) => u.data.hireDate?.toMillis()) || 0,
      dir: 'asc'
    }
  },

  runDate: {
    key: 'runDate',
    label: 'Run date',
    groups: { sort: (g) => g.key, dir: 'asc' },
    items: {
      sort: (d, o) =>
        d.exp.data.automations[o.autoTplId].timing.at.toMillis() || 0,
      dir: 'desc'
    }
  }
};

const ROW_TO_KEY = (d: D) => d.exp.id;
const DEFAULT_SORTER = SORTERS.runDate;

export const AutomationsExecutionReportTable = ({
  instances,
  autoTplId,
  autoTplTitle,
  tpl,
  actor,
  onShowInstanceDetails
}: {
  instances: Doc<IExperience>[];
  autoTplId: string;
  autoTplTitle: string;
  tpl: Doc<IExperienceTemplate>;
  actor: { key: string; label: string };
  onShowInstanceDetails: (d: Doc<IExperience>) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { trackEvent } = useProductAnalytics();
  const peopleTabV2Expand = usePeopleTabV2Enabled();
  const currentUser = useCurrentUser();
  const { org } = currentUser;
  const { sortBy, direction, setSort } = useSort<ItemSorter<D, OtherProps>>(
    DEFAULT_SORTER,
    DEFAULT_SORTER.items.dir
  );

  const weNeedGrouping = sortBy.key === 'runDate';

  const userIds = useMemo(
    () => uniq(flatten(instances.map((d) => getAllActorUserIds(d)))),
    [instances]
  );

  const toRows = useCallback(
    (userMap: { [key: string]: Doc<IUser> }) =>
      instances.map<D>((exp) => {
        const actors: { [actorKey: string]: Doc<IUser> } = {};
        exp.data.actors.filter(isExperienceUserActor).forEach((a) => {
          actors[a.key] = userMap[a.userId] || null;
        });
        return {
          exp,
          actors: actors
        };
      }),
    [instances]
  );

  const [rows] = useMappedLoadingValue(
    useUserMapById(instances[0].data.orgId, userIds),
    toRows,
    true
  );

  const otherProps: OtherProps = useMemo(
    () => ({ tpl, autoTplId, actor, onShowInstanceDetails, trackEvent }),
    [autoTplId, actor, tpl, onShowInstanceDetails, trackEvent]
  );

  const groups = useMemo(() => {
    if (!rows) return [];
    const grouped = groupBy(rows, (r) => {
      return moment(r.exp.data.automations[autoTplId].timing.at.toMillis())
        .startOf('d')
        .valueOf();
    });
    return orderBy(
      Object.keys(grouped).map((group) => ({
        key: group,
        rows: grouped[group]
      })),
      (s) => parseInt(s.key),
      direction
    );
  }, [rows, autoTplId, direction]);

  const columns =
    actor.key === PARTICIPANT_ACTOR_KEY
      ? PARTICIPANT_COLUMNS
      : SUPPORTING_ACTOR_COLUMNS;

  if (!rows) {
    return null;
  }
  // v2: rows are projection-backed pseudo-instances (no expand content) → lazy-load
  // the full instance per expanded row. v1: precompute from the full instances.
  const rowsWithAutomations = peopleTabV2Expand
    ? new Map<string, JSX.Element>(
        rows.map((row) => [
          row.exp.id,
          <LazyAutomationRowExpand row={row} autoTplId={autoTplId} tpl={tpl} org={org} />
        ])
      )
    : formatAutomationToExpandableComponent(rows, autoTplId, tpl, org);

  if (weNeedGrouping) {
    return (
      <GroupedAutomationTable
        groups={groups}
        renderGroupDivider={(group) => (
          <div>
            {t('Run date')}:{' '}
            {moment(parseInt(group.key)).format(org.dateFormats.MMDDYYYY)}
          </div>
        )}
        columns={columns}
        sortBy={sortBy}
        direction={direction}
        otherProps={otherProps}
        setSort={setSort}
        sidePadding
        expandableElements={rowsWithAutomations}
        onExpansionClick={(rowId: string) =>
          trackEvent({
            eventType: PAEventType.EXPERIENCE_TPL,
            name: 'Timeline Item Modal People Tab Item Expanded',
            Explanation:
              'Admin user expands a row in the People Tab, to see the details of the timeline item in one instance',
            'Timeline Item ID': autoTplId,
            'Experience Template ID': tpl.id,
            'Timeline Item Title': autoTplTitle,
            'Timeline Item Type': 'AUTOMATION',
            'Status of Expanded Item':
              rows.find((el) => el.exp.id === rowId)?.exp.data.automations[
                autoTplId
              ]?.status || 'NOT FOUND'
          })
        }
      />
    );
  }

  return (
    <RowsRenderer
      variant="contained"
      columns={columns}
      rows={rows}
      rowToKey={ROW_TO_KEY}
      sorter={sortBy}
      sortDirection={direction}
      renderHead={true}
      headProps={{ sticky: true }}
      onHeadClick={(c, dir) => {
        const sorter = SORTERS[c.key];
        if (!sorter) {
          return;
        }
        setSort(sorter, dir || sorter.items.dir);
      }}
      chunkSize={30}
      rootMargin="300px"
      rowHeight={ROW_HEIGHTS.dense}
      otherProps={otherProps}
      expandableElements={rowsWithAutomations}
      sidePadding
      onExpansionClick={(rowId: string) =>
        trackEvent({
          eventType: PAEventType.EXPERIENCE_TPL,
          name: 'Timeline Item Modal People Tab Item Expanded',
          Explanation:
            'Admin user expands a row in the People Tab, to see the details of the timeline item in one instance',
          'Timeline Item ID': autoTplId,
          'Experience Template ID': tpl.id,
          'Timeline Item Title': autoTplTitle,
          'Timeline Item Type': 'AUTOMATION',
          'Status of Expanded Item':
            rows.find((el) => el.exp.id === rowId)?.exp.data.automations[
              autoTplId
            ]?.status || 'NOT FOUND'
        })
      }
    />
  );
};

const GroupedAutomationTable = ({
  groups,
  renderGroupDivider,
  columns,
  sortBy,
  direction,
  otherProps,
  setSort,
  sidePadding,
  expandableElements,
  onExpansionClick
}: {
  groups: Group[];
  renderGroupDivider: (group: Group) => React.ReactNode;
  columns: Column[];
  sortBy: ItemSorter<D, OtherProps>;
  direction: SortDirection;
  otherProps: OtherProps;
  setSort: (
    nextSortBy: ItemSorter<D, OtherProps>,
    nextDirection: SortDirection
  ) => void;
  sidePadding?: boolean;
  expandableElements?: Map<string, JSX.Element>;
  onExpansionClick?: (rowId: string) => Promise<void>;
}) => {
  return (
    <>
      <RowsRenderer
        variant="contained"
        columns={columns}
        rows={[]}
        renderHead={true}
        sorter={sortBy}
        sortDirection={direction}
        otherProps={otherProps}
        rowToKey={ROW_TO_KEY}
        onHeadClick={(c, dir) => {
          const sorter = SORTERS[c.key];
          if (!sorter) {
            return;
          }
          setSort(sorter, dir || sorter.items.dir);
        }}
        sidePadding={sidePadding}
      />
      {groups.map((group) => {
        if (!group.rows.length) return null;
        return (
          <div key={group.key}>
            <GroupDivider>{renderGroupDivider(group)}</GroupDivider>
            <RowsRenderer
              variant="contained"
              columns={columns}
              rows={group.rows}
              rowToKey={ROW_TO_KEY}
              sorter={sortBy}
              sortDirection={direction}
              renderHead={false}
              headProps={{ sticky: true }}
              onHeadClick={(c, dir) => {
                const sorter = SORTERS[c.key];
                if (!sorter) {
                  return;
                }
                setSort(sorter, dir || sorter.items.dir);
              }}
              chunkSize={30}
              rootMargin="300px"
              rowHeight={ROW_HEIGHTS.dense}
              otherProps={otherProps}
              expandableElements={expandableElements}
              sidePadding={sidePadding}
              onExpansionClick={onExpansionClick}
            />
          </div>
        );
      })}
    </>
  );
};

const ActionStatusChip = <
  T extends {
    // Display status: the runtime AutomationActionStatus plus the synthetic
    // 'RUNNING' the denormalizer adds for an in-flight sync action.
    status: AutomationActionStatus | 'RUNNING';
    errorData?: any;
  }
>({
  action
}: {
  action: T;
}) => {
  const { t } = useUnsafeTranslation();
  switch (action.status) {
    case AutomationActionStatus.DONE:
      return (
        <ColoredChipLight color="green" size="small">
          {t('Completed')}
        </ColoredChipLight>
      );
    case AutomationActionStatus.SKIPPED:
      return (
        <ColoredChipLight color="darkGray" size="small">
          {t('Skipped')}
        </ColoredChipLight>
      );
    case 'RUNNING':
      return (
        <ColoredChipLight color="blue" size="small">
          {t('Running')}
        </ColoredChipLight>
      );
    case AutomationActionStatus.UPCOMING:
      return (
        <ColoredChipLight color="gray" size="small">
          {t('Pending')}
        </ColoredChipLight>
      );
    case AutomationActionStatus.ERROR:
      return (
        <ColoredChipLight color="red" size="small">
          {t('Error')}
        </ColoredChipLight>
      );
    default:
      return null;
  }
};

export const ExtendableAutomationsActions = ({
  actions,
  supportingActors
}: {
  actions: AutomationActionDenormalized[];
  supportingActors: IExperienceTemplate['supportingActors'];
}) => {
  return (
    <SubItemContainer>
      <InnerItemList>
        <InnerItemGroup>
          {(actions || []).map((ac: AutomationActionDenormalized) => {
            return (
              <InnerItemRow key={ac.id}>
                <FlexContainer
                  justifyContent={'space-between'}
                  fullWidth={true}
                >
                  <InnerItemDetail>
                    <ActionStatusChip action={ac} />

                    <ActionWithIcon action={ac} />
                    {ac.status === AutomationActionStatus.ERROR && (
                      <ErrorText>
                        <ErrorAlert>
                          <AlertCircle size={18} />
                          <ActionErrorDetails
                            action={ac}
                            supportingActors={supportingActors}
                          />
                        </ErrorAlert>
                      </ErrorText>
                    )}
                  </InnerItemDetail>
                </FlexContainer>
              </InnerItemRow>
            );
          })}
        </InnerItemGroup>
      </InnerItemList>
    </SubItemContainer>
  );
};
